import { useEffect, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router';

import DiscardChangesDialog from '@/components/DiscardChangesDialog';
import { useArticlesConditionForms } from '@/models/article';
import { useProductAttributesByArticle } from '@/models/productAttribute';
import { useRequest } from '@/models/request';
import { useWorkflowPublic } from '@/models/workflow';
import {
  RequestContextProvider,
  useRequestContext,
} from '@/routes/Requests/contexts/RequestContext';
import { useShowWarningBeforeLeavingApp, useSimpleBlocker } from '@/utils/navigation';

import { DropoffSignature } from './components/DropoffSignature';
import { RequestForm } from './components/RequestForm';

import './New.css';

const New = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: request, isLoading: isRequestLoading, isError } = useRequest(id);
  const { data: workflow } = useWorkflowPublic(request?.workflowId);
  const conditionFormsQueries = useArticlesConditionForms(
    { articleIds: request?.articles.map((article) => article.id) ?? [] },
    { enabled: !!workflow?.conditionForms?.find(({ type }) => type === 'initial_condition')?.id }
  );
  const productAttributesByArticle = useProductAttributesByArticle(request);

  useEffect(() => {
    if (isError) {
      navigate('/requests', { state: { skipRouterBlocker: true, replace: true } });
    }
  }, [navigate, isError]);

  useShowWarningBeforeLeavingApp();

  if (isRequestLoading) {
    return null;
  }

  if (!request || !workflow) {
    return null;
  }

  return (
    <RequestContextProvider
      request={request}
      workflow={workflow}
      initialConditionFormsData={request?.articles.map((article, index) => ({
        articleId: article.id,
        formData: conditionFormsQueries[index]?.data,
      }))}
      productAttributesData={productAttributesByArticle}
    >
      <NewContent />
    </RequestContextProvider>
  );
};

const NewContent = () => {
  const { request, view } = useRequestContext();

  const [newRequestStep, setNewRequestStep] = useState<'form' | 'signature'>('form');

  // Block going away
  const blocker = useSimpleBlocker();

  return (
    <>
      {newRequestStep === 'form' && (
        <RequestForm
          onNext={
            view.client.dropoffSignature.enabled ? () => setNewRequestStep('signature') : undefined
          }
        />
      )}
      {newRequestStep === 'signature' && (
        <DropoffSignature onBack={() => setNewRequestStep('form')} />
      )}
      <Outlet context={request} />
      {blocker.state === 'blocked' && (
        <DiscardChangesDialog
          onConfirm={() => blocker.proceed()}
          onCancel={() => blocker.reset()}
        />
      )}
    </>
  );
};

export default New;
